import { motion } from "motion/react";
import { Wallet, ArrowRight, Lock, Mail, User, Building2 } from "lucide-react";
import { useState } from "react";

import { useRWAStore, UserRole } from "../store/rwa-store";


type AuthMode = 'login' | 'register';

export default function AuthView() {
    const { login, register } = useRWAStore();
    const [mode, setMode] = useState<AuthMode>('login');
    const [loading, setLoading] = useState(false);

    // Form State
    const [email, setEmail] = useState("admin@platform.com"); // Pre-filled for demo speed
    const [name, setName] = useState("");
    const [role, setRole] = useState<UserRole>('investor');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setTimeout(() => {
            if (mode === 'login') {
                login(email);
            } else {
                register(name, email, role);
            }
            setLoading(false);
        }, 1200);
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] w-full max-w-md mx-auto">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8 backdrop-blur-xl"
            >
                <div className="text-center mb-6">
                    <div className="mx-auto w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4">
                        <Lock className="w-6 h-6 text-emerald-500" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">{mode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
                    <p className="text-neutral-400 text-sm">Secure Institutional Access</p>
                </div>

                {/* --- TABS --- */}
                <div className="flex bg-neutral-900/50 p-1 rounded-lg mb-6 border border-neutral-800">
                    <button
                        onClick={() => setMode('login')}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${mode === 'login' ? 'bg-neutral-800 text-white shadow-xs' : 'text-neutral-500 hover:text-neutral-300'}`}
                    >
                        Login
                    </button>
                    <button
                        onClick={() => setMode('register')}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${mode === 'register' ? 'bg-neutral-800 text-white shadow-xs' : 'text-neutral-500 hover:text-neutral-300'}`}
                    >
                        Register
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'register' && (
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">Full Name</label>
                            <div className="relative">
                                <User className="absolute left-3 top-3 w-4 h-4 text-neutral-500" />
                                <input
                                    required
                                    type="text"
                                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg py-2.5 pl-10 pr-4 text-sm focus:outline-hidden focus:border-emerald-500 hover:border-neutral-700 transition-colors"
                                    placeholder="John Doe"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">Email Address</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-3 w-4 h-4 text-neutral-500" />
                            <input
                                required
                                type="email"
                                className="w-full bg-neutral-950 border border-neutral-800 rounded-lg py-2.5 pl-10 pr-4 text-sm focus:outline-hidden focus:border-emerald-500 hover:border-neutral-700 transition-colors"
                                placeholder="name@company.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                            />
                        </div>
                    </div>

                    {mode === 'register' && (
                        <div className="grid grid-cols-2 gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setRole("investor")}
                                className={`flex flex-col items-center p-3 rounded-xl border transition-all ${role === "investor"
                                        ? "bg-emerald-500/10 border-emerald-500/50 text-white"
                                        : "bg-neutral-900 border-neutral-800 text-neutral-500 hover:border-neutral-700"
                                    }`}
                            >
                                <Wallet className="w-5 h-5 mb-2" />
                                <span className="text-xs font-medium">Investor</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setRole("issuer")}
                                className={`flex flex-col items-center p-3 rounded-xl border transition-all ${role === "issuer"
                                        ? "bg-blue-500/10 border-blue-500/50 text-white"
                                        : "bg-neutral-900 border-neutral-800 text-neutral-500 hover:border-neutral-700"
                                    }`}
                            >
                                <Building2 className="w-5 h-5 mb-2" />
                                <span className="text-xs font-medium">Issuer</span>
                            </button>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-colors flex items-center justify-center gap-2 mt-4"
                    >
                        {loading ? (
                            <span className="animate-pulse">Processing...</span>
                        ) : (
                            <>
                                {mode === 'login' ? 'Access Terminal' : 'Create Wallet'} <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </form>

                {mode === 'login' && (
                    <div className="mt-6 text-center text-xs text-neutral-600">
                        <p>Demo Credentials:</p>
                        <div className="font-mono mt-1">admin@platform.com</div>
                    </div>
                )}

            </motion.div>
        </div>
    );
}
